/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement, useState } from "react"
import { observer } from "mobx-react-lite"
import { LoadingButton } from "@mui/lab"
import { logError } from "@medaica/common/services/util"
import { useHeartReportStores } from "../heart-report-context"
import { HeartReportCard, SupportLink } from "../components/heart-report-layout"
import heartReportCopy from "../heartReportCopy"

const copy = heartReportCopy.requestCode

/**
 * Landing screen. Shows nothing about the patient: the masked email is only known once a
 * code has been sent (the API returns it from the same call that sends the code), so the
 * subtitle names "the email address this report was sent to" and the next screen shows
 * the masked address. The code is sent on click, never on load.
 */
const RequestCode = observer((): ReactElement => {
  const { sessionStore } = useHeartReportStores()
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const send = async (): Promise<void> => {
    setSending(true)
    setError(null)
    try {
      await sessionStore.sendCode()
      // navigation is handled by the store (→ verify, or unavailable for a dead link)
    } catch (sendError) {
      logError(sendError)
      setError(copy.errors.sendFailed)
    } finally {
      setSending(false)
    }
  }

  return (
    <HeartReportCard title={copy.title}>
      {sessionStore.notice ? (
        <output className="mt-4 block text-center text-sm text-amber-700">{copy.notices[sessionStore.notice]}</output>
      ) : null}
      <p className="mt-2 mb-8 text-center text-gray-600">{copy.subtitle}</p>

      {error ? (
        <p role="alert" className="mb-3 text-center text-sm text-red-600">
          {error}
        </p>
      ) : null}

      <LoadingButton fullWidth variant="contained" size="large" loading={sending} onClick={() => void send()}>
        {copy.sendButton}
      </LoadingButton>

      <p className="mt-5 text-center text-sm text-gray-500">
        {copy.notYouPrompt} <SupportLink>{copy.contactSupport}</SupportLink>
      </p>
    </HeartReportCard>
  )
})

export default RequestCode
